import { PostgrestClient } from "@supabase/postgrest-js";
import { HoldingAreaEntry } from "../types";

export class HoldingAreaService {
  private postgrest: PostgrestClient;

  constructor(postgrest: PostgrestClient) {
    this.postgrest = postgrest;
  }

  /**
   * Add a tenant to the holding area
   * Creates a new entry with status='active'
   */
  async addToHoldingArea(
    tenantId: string,
    fboId: string
  ): Promise<HoldingAreaEntry> {
    const { data, error } = await this.postgrest
      .from("holding_area_entry")
      .insert({
        tenant_id: tenantId,
        fbo_id: fboId,
        status: "active",
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to add to holding area: ${error.message}`);
    }

    return data;
  }

  /**
   * Remove a tenant from the holding area
   * Updates status to 'removed'
   */
  async removeFromHoldingArea(tenantId: string): Promise<HoldingAreaEntry> {
    // Find the active entry for this tenant
    const { data: entry, error: findError } = await this.postgrest
      .from("holding_area_entry")
      .select()
      .eq("tenant_id", tenantId)
      .eq("status", "active")
      .single();

    if (findError) {
      throw new Error(`Failed to find holding area entry: ${findError.message}`);
    }

    if (!entry) {
      throw new Error(`No active holding area entry found for tenant ${tenantId}`);
    }

    const { data, error } = await this.postgrest
      .from("holding_area_entry")
      .update({ status: "removed" })
      .eq("id", entry.id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to remove from holding area: ${error.message}`);
    }

    return data;
  }

  /**
   * Assign a tenant from the holding area to a hangar
   * Updates status to 'assigned'
   */
  async assignFromHoldingArea(tenantId: string): Promise<HoldingAreaEntry> {
    // Find the active entry for this tenant
    const { data: entry, error: findError } = await this.postgrest
      .from("holding_area_entry")
      .select()
      .eq("tenant_id", tenantId)
      .eq("status", "active")
      .single();

    if (findError) {
      throw new Error(`Failed to find holding area entry: ${findError.message}`);
    }

    if (!entry) {
      throw new Error(`No active holding area entry found for tenant ${tenantId}`);
    }

    const { data, error } = await this.postgrest
      .from("holding_area_entry")
      .update({ status: "assigned" })
      .eq("id", entry.id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to assign from holding area: ${error.message}`);
    }

    return data;
  }

  /**
   * Get all active holding area entries for an FBO
   * Returns entries with full tenant data
   */
  async getActiveEntries(fboId: string): Promise<HoldingAreaEntry[]> {
    const { data, error } = await this.postgrest
      .from("holding_area_entry")
      .select(`
        *,
        tenant:tenant_id (
          *,
          aircraft:aircraft_id (*),
          position:entity_id!inner(*)
        )
      `)
      .eq("fbo_id", fboId)
      .eq("status", "active")
      .order("added_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to get holding area entries: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get all holding area entries (including historical) for an FBO
   * Useful for audit trail
   */
  async getAllEntries(fboId: string): Promise<HoldingAreaEntry[]> {
    const { data, error } = await this.postgrest
      .from("holding_area_entry")
      .select(`
        *,
        tenant:tenant_id (
          *,
          aircraft:aircraft_id (*),
          position:entity_id!inner(*)
        )
      `)
      .eq("fbo_id", fboId)
      .order("added_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to get holding area history: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get a specific holding area entry by tenant ID
   * Returns the active entry for the tenant if it exists
   */
  async getEntryByTenantId(tenantId: string): Promise<HoldingAreaEntry | null> {
    const { data, error } = await this.postgrest
      .from("holding_area_entry")
      .select(`
        *,
        tenant:tenant_id (
          *,
          aircraft:aircraft_id (*),
          position:entity_id!inner(*)
        )
      `)
      .eq("tenant_id", tenantId)
      .eq("status", "active")
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to get holding area entry: ${error.message}`);
    }

    return data;
  }

  /**
   * Check if a tenant is currently in the holding area
   */
  async isInHoldingArea(tenantId: string): Promise<boolean> {
    const { data, error } = await this.postgrest
      .from("holding_area_entry")
      .select("id")
      .eq("tenant_id", tenantId)
      .eq("status", "active")
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to check holding area status: ${error.message}`);
    }

    return Boolean(data);
  }
}
